import { Archive, Forum, MessagesSquare, Plus, Tag } from '@/components/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { createForumPostSchema, LIMITS, type ChannelDTO, type ForumPostDTO, type ForumTagDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { forumApi } from '@/api/endpoints';
import { MessageContent } from '@/components/chat/MessageContent';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Feedback';
import { Input, TextArea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useSubmit, validate } from '@/hooks/useForm';
import { useChannelPermissions } from '@/lib/permissions';
import { formatRelative } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useReadStates } from '@/stores/social';
import { isThreadUnread, threadPath, useThreads } from '@/stores/threads';

/** One of a forum's tags, as a chip (pressable when `onToggle` is given). */
export function TagChip({ tag, active, onToggle }: { tag: ForumTagDTO; active?: boolean; onToggle?: () => void }) {
  const className = cn(
    'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold transition-colors',
    active ? 'border-accent bg-accent-soft text-fg' : 'border-line bg-sunken text-fg-soft',
    onToggle && !active && 'hover:border-line-strong',
  );
  const label = (
    <>
      {tag.emoji && <span aria-hidden>{tag.emoji}</span>}
      {tag.name}
    </>
  );
  if (!onToggle) return <span className={className}>{label}</span>;
  return (
    <button type="button" aria-pressed={!!active} onClick={onToggle} className={className}>
      {label}
    </button>
  );
}

/**
 * A forum channel: its guidelines, its posts (most recently active first, with
 * a peek at each one's first message), filters by tag, and a button for a new post.
 * Opening a post shows it like a thread.
 */
export function ForumView({ channel, openThreadId }: { channel: ChannelDTO; openThreadId?: string }) {
  const perms = useChannelPermissions(channel.id);
  const navigate = useNavigate();
  const [previews, setPreviews] = useState<Record<string, ForumPostDTO['firstMessage']> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const threads = useThreads(useShallow((s) => Object.values(s.threads).filter((t) => t.parentId === channel.id)));
  const reads = useReadStates((s) => s.channels);
  const reloading = useRef(false);

  const load = useCallback(async () => {
    reloading.current = true;
    try {
      const { posts, readStates } = await forumApi.posts(channel.id);
      useThreads.getState().upsert(posts);
      for (const r of readStates) useReadStates.getState().markRead(r.channelId, r.lastReadAt);
      setPreviews((current) => ({ ...current, ...Object.fromEntries(posts.map((p) => [p.id, p.firstMessage])) }));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      reloading.current = false;
    }
  }, [channel.id]);

  useEffect(() => {
    setPreviews(null);
    void load();
  }, [load]);

  // New posts (from anyone) arrive as threads: fetch their first messages too.
  useEffect(() => {
    if (!previews || reloading.current) return;
    if (threads.some((t) => !(t.id in previews))) void load();
  }, [threads, previews, load]);

  const posts = useMemo(
    () =>
      threads
        .filter((t) => !tagFilter || t.tagIds.includes(tagFilter))
        .sort((a, b) => {
          if (!!a.archivedAt !== !!b.archivedAt) return a.archivedAt ? 1 : -1;
          return (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt);
        }),
    [threads, tagFilter],
  );
  const tagsById = useMemo(() => new Map(channel.forumTags.map((t) => [t.id, t])), [channel.forumTags]);
  const canPost = perms.can('SEND_MESSAGES');

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-8 sm:px-6">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {canPost && (
              <Button icon={<Plus size={16} />} onClick={() => setCreating(true)}>
                New post
              </Button>
            )}
            {channel.forumTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by tag">
                <Tag size={15} className="text-fg-muted" />
                {channel.forumTags.map((tag) => (
                  <TagChip key={tag.id} tag={tag} active={tagFilter === tag.id} onToggle={() => setTagFilter(tagFilter === tag.id ? null : tag.id)} />
                ))}
              </div>
            )}
          </div>
          {channel.topic && (
            <div className="rounded-2xl border border-line bg-sidebar p-4">
              <p className="mb-1 text-xs font-bold tracking-wide text-fg-muted uppercase">Guidelines</p>
              <MessageContent content={channel.topic} serverId={channel.serverId} className="text-sm text-fg-soft" />
            </div>
          )}
          {error ? (
            <p className="text-sm text-danger">{error}</p>
          ) : !previews ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : posts.length === 0 ? (
            <EmptyState
              icon={<Forum size={26} />}
              title={tagFilter ? 'No posts with that tag' : 'No posts yet'}
              body={tagFilter ? 'Try another tag, or clear the filter.' : canPost ? 'Start the first one: ask a question or kick off a topic.' : 'Posts will show up here.'}
            />
          ) : (
            <ul className="flex flex-col gap-2.5">
              {posts.map((post) => {
                const preview = previews[post.id];
                const unread = isThreadUnread(post, reads[post.id]?.lastReadAt);
                const replies = Math.max(0, post.messageCount - 1);
                return (
                  <li key={post.id}>
                    <button
                      type="button"
                      onClick={() => navigate(threadPath(post))}
                      aria-current={post.id === openThreadId ? 'page' : undefined}
                      className={cn(
                        'block w-full rounded-2xl border bg-sidebar p-4 text-left transition-colors hover:border-line-strong hover:bg-hover/40',
                        post.id === openThreadId ? 'border-accent' : 'border-line',
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <h3 className={cn('min-w-0 flex-1 text-[15px] break-words', unread ? 'font-extrabold text-fg' : 'font-bold text-fg-soft')}>{post.name}</h3>
                        {post.archivedAt && (
                          <span className="flex shrink-0 items-center gap-1 text-xs text-fg-muted">
                            <Archive size={13} /> Archived
                          </span>
                        )}
                      </div>
                      {post.tagIds.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {post.tagIds.map((id) => {
                            const tag = tagsById.get(id);
                            return tag ? <TagChip key={id} tag={tag} /> : null;
                          })}
                        </div>
                      )}
                      {preview && (
                        <div className="mt-2 line-clamp-2 text-sm text-fg-muted">
                          <MessageContent content={preview.content} serverId={channel.serverId} interactive={false} className="inline" />
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-2 text-xs text-fg-muted">
                        {preview?.author && <Avatar user={preview.author} size={18} decorate={false} />}
                        <span className="truncate font-semibold text-fg-soft">{preview?.author?.displayName ?? 'Someone'}</span>
                        <span className="flex items-center gap-1">
                          <MessagesSquare size={13} /> {replies} {replies === 1 ? 'reply' : 'replies'}
                        </span>
                        <span className="ml-auto shrink-0">{formatRelative(post.lastMessageAt ?? post.createdAt)}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      {creating && (
        <NewPostModal
          channel={channel}
          onClose={() => setCreating(false)}
          onCreated={(post) => {
            setPreviews((current) => ({ ...current, [post.id]: post.firstMessage }));
            useThreads.getState().upsert([post]);
            setCreating(false);
            navigate(threadPath(post));
          }}
        />
      )}
    </div>
  );
}

function NewPostModal({ channel, onClose, onCreated }: { channel: ChannelDTO; onClose: () => void; onCreated: (post: ForumPostDTO) => void }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const { loading, error, fields, setFields, run } = useSubmit();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(createForumPostSchema, { title, content, tagIds });
    if (!data) return setFields(invalid);
    const post = await run(() => forumApi.create(channel.id, data));
    if (post) onCreated(post);
  };

  const toggle = (id: string) => setTagIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : ids.length < LIMITS.forumPostTagsMax ? [...ids, id] : ids));

  return (
    <Modal
      open
      onClose={onClose}
      title={`New post in #${channel.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="new-post" loading={loading}>
            Post
          </Button>
        </>
      }
    >
      <form id="new-post" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        {error && <p className="text-sm text-danger">{error}</p>}
        <Input label="Title" data-autofocus maxLength={LIMITS.threadNameMax} value={title} onChange={(e) => setTitle(e.target.value)} error={fields.title} placeholder="What's it about?" />
        {channel.forumTags.length > 0 && (
          <fieldset>
            <legend className="mb-2 pl-1 text-[13px] font-semibold text-fg-soft">Tags</legend>
            <div className="flex flex-wrap gap-1.5">
              {channel.forumTags.map((tag) => (
                <TagChip key={tag.id} tag={tag} active={tagIds.includes(tag.id)} onToggle={() => toggle(tag.id)} />
              ))}
            </div>
          </fieldset>
        )}
        <TextArea
          label="Message"
          rows={6}
          maxLength={LIMITS.messageMax}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          error={fields.content}
          placeholder="Give people the details. **Bold**, *italic*, > quotes and links work."
        />
      </form>
    </Modal>
  );
}
